import * as React from 'react';
import { MoreHorizontal, User } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Deal } from '../../types/pipeline.types';
import { useAvatar } from '../../hooks/use-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

interface DealCardProps {
  deal: Deal;
  isSelected?: boolean;
  onClick?: (deal: Deal) => void;
  onEdit?: (dealId: string) => void;
  onChangeStage?: (dealId: string) => void;
  onDelete?: (dealId: string) => void;
  isDragging?: boolean;
}

function OwnerAvatar({
  user,
  label,
}: {
  user: Deal['setter'] | Deal['closer'];
  label: string;
}) {
  const [imgError, setImgError] = React.useState(false);
  const avatarUrl = useAvatar(user?.imageName);
  const showImage = avatarUrl && !imgError;

  return (
    <div className="flex items-center gap-2 min-w-0">
      {showImage ? (
        <img
          src={avatarUrl}
          alt={user?.name}
          className="size-8 rounded-full object-cover shrink-0"
          onError={() => setImgError(true)}
        />
      ) : user ? (
        <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-[10px] font-semibold text-primary">
            {user.initials}
          </span>
        </div>
      ) : (
        <div className="size-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          <User className="size-4 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-xs font-medium text-foreground truncate">
          {user?.name || 'Unassigned'}
        </p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export function DealCard({
  deal,
  isSelected,
  onClick,
  onEdit,
  onChangeStage,
  onDelete,
  isDragging,
}: DealCardProps) {
  const renderOwner = (
    user: Deal['setter'] | Deal['closer'],
    label: string
  ) => {
    return <OwnerAvatar user={user} label={label} />;
  };

  const renderAttributes = () => {
    if (!deal.attrs?.length) {
      return null;
    }
    return (
      <div className="space-y-0.5">
        {deal.attrs.map((attr, i) => (
          <p key={i} className="text-sm text-muted-foreground truncate">
            {typeof attr === 'string' ? attr : (attr as any).S}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'group relative rounded-xl border bg-card p-4 transition-all cursor-grab active:cursor-grabbing',
        'hover:shadow-md hover:border-primary/30',
        isSelected && 'border-primary ring-1 ring-primary/20',
        isDragging && 'shadow-lg rotate-1 scale-[1.02] opacity-90'
      )}
      onClick={() => onClick?.(deal)}
    >
      {/* Header: Name + Actions */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <h4 className="font-semibold text-sm text-foreground line-clamp-2 flex-1">
          {deal.name}
        </h4>
        <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="size-7 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(deal.id)}>
                  Edit
                </DropdownMenuItem>
              )}
              {onChangeStage && (
                <DropdownMenuItem onClick={() => onChangeStage(deal.id)}>
                  Change Stage
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete(deal.id)}
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Attributes (address, email, phone) */}
      {renderAttributes()}

      {/* Owners: Setter + Closer side by side */}
      <div className="mt-3 flex items-center gap-4">
        {renderOwner(deal.setter, 'Setter')}
        {renderOwner(deal.closer, 'Closer')}
      </div>
    </div>
  );
}
